import React, { useCallback, useEffect, useMemo, useState, useRef } from 'react';
import { createEditor, Transforms, Editor, Text, Range, Point } from 'slate';
import { Slate, Editable, withReact, useSlate, ReactEditor } from 'slate-react';
import { withHistory } from 'slate-history';
import isHotkey from 'is-hotkey';
import style from './TextEditor.module.css';
import { selectLoader } from 'utils/Helpers';
import { useAuth } from 'contexts/AuthContext';
import APIServices from 'services/APIServices';
import { Element as SlateElement } from 'slate';
// Import icons
import boldIcon from 'assets/img/boldIcon.svg';
import italicIcon from 'assets/img/italicIcon.svg';
import underlineIcon from 'assets/img/link.svg';
import imageIcon from 'assets/img/file-picture.svg';
import bulletIcon from 'assets/img/bullet-list.svg';
import numberlistIcon from 'assets/img/numberlistIcon.svg';

const HOTKEYS = {
  'mod+b': 'bold',
  'mod+i': 'italic',
  'mod+u': 'underline'
};

const LIST_TYPES = ['numbered-list', 'bulleted-list'];

const Leaf = ({ attributes, children, leaf }) => {
  if (leaf.bold) children = <strong>{children}</strong>;
  if (leaf.italic) children = <em>{children}</em>;
  if (leaf.underline) children = <u>{children}</u>;
  return <span {...attributes}>{children}</span>;
};

const ToolbarButton = ({ format, icon, title }) => {
  const editor = useSlate();
  const isActive = isFormatActive(editor, format);

  return (
    <button
      className={`${style.toolbarButton} ${isActive ? style.active : ''}`}
      title={title}
      onMouseDown={(e) => {
        e.preventDefault();
        toggleFormat(editor, format);
      }}>
      <img src={icon} alt={title} className={style.toolbarIcon} />
    </button>
  );
};

const ImageButton = ({ onImageUpload }) => {
  return (
    <button
      className={style.toolbarButton}
      title="Insert Image"
      onMouseDown={(e) => {
        e.preventDefault();
        onImageUpload();
      }}>
      <img src={imageIcon} alt="Insert Image" className={style.toolbarIcon} />
    </button>
  );
};
const LinkButton = () => {
  const editor = useSlate();

  return (
    <button
      className={style.toolbarButton}
      title="Insert Link"
      onMouseDown={(e) => {
        e.preventDefault();
        const url = window.prompt('Enter the URL of the link:');
        if (!url) return;
        insertLink(editor, url);
      }}>
      <img src={underlineIcon} alt="Insert Link" className={style.toolbarIcon} />
    </button>
  );
};

const Toolbar = ({ onImageUpload }) => {
  return (
    <div className={style.toolbar}>
      <ToolbarButton format="bold" icon={boldIcon} title="Bold (Ctrl+B)" />
      <ToolbarButton format="italic" icon={italicIcon} title="Italic (Ctrl+I)" />
      <ToolbarButton format="underline" icon={underlineIcon} title="Underline (Ctrl+U)" />
      <ToolbarButton format="bulleted-list" icon={bulletIcon} title="Bullet List" />
      {/* <ToolbarButton format="numbered-list" icon={numberlistIcon} title="Numbered List" /> */}
      {/* <LinkButton /> */}
      {/* <ImageButton onImageUpload={onImageUpload} /> */}
    </div>
  );
};

const MentionDropdown = ({ target, filteredMentions, index, onSelect }) => {
  if (!target || filteredMentions.length === 0) return null;

  return (
    <div
      className={style.mentionDropdown}
      onMouseDown={(e) => {
        // prevent the wrapper itself from stealing focus when clicked
        e.preventDefault();
      }}>
      {filteredMentions.map((member, i) => (
        <div
          key={member.id}
          className={`${style.mentionDropdownItem} ${i === index ? style.mentionDropdownItemActive : ''}`}
          onMouseDown={(e) => {
            // IMPORTANT: prevent blur and stop propagation, then call onSelect
            e.preventDefault();
            e.stopPropagation();
            onSelect(member);
          }}>
          @{member.value}
        </div>
      ))}
    </div>
  );
};

const toggleBlockFormat = (editor, format) => {
  const isActive = isBlockActive(editor, format);
  const isList = LIST_TYPES.includes(format);

  Transforms.unwrapNodes(editor, {
    match: (n) => !Editor.isEditor(n) && SlateElement.isElement(n) && LIST_TYPES.includes(n.type),
    split: true
  });

  const newType = isActive ? 'paragraph' : isList ? 'list-item' : format;

  Transforms.setNodes(editor, { type: newType });

  if (!isActive && isList) {
    const block = { type: format, children: [] };
    Transforms.wrapNodes(editor, block);
  }
};
const isFormatActive = (editor, format) => {
  const [match] = Editor.nodes(editor, {
    match: (n) => n[format] === true,
    universal: true
  });
  return !!match;
};
const toggleInlineFormat = (editor, format) => {
  const isActive = isInlineFormatActive(editor, format);
  Transforms.setNodes(editor, { [format]: isActive ? null : true }, { match: Text.isText, split: true });
};

const isInlineFormatActive = (editor, format) => {
  const [match] = Editor.nodes(editor, {
    match: (n) => Text.isText(n) && n[format] === true,
    universal: true
  });
  return !!match;
};
const toggleFormat = (editor, format) => {
  if (LIST_TYPES.includes(format)) {
    toggleBlockFormat(editor, format);
  } else {
    toggleInlineFormat(editor, format);
  }
};

const isBlockActive = (editor, format) => {
  const [match] = Editor.nodes(editor, {
    match: (n) => SlateElement.isElement(n) && n.type === format
  });

  return !!match;
};

const insertImage = (editor, url) => {
  const image = {
    type: 'image',
    url,
    children: [{ text: '' }]
  };
  Transforms.insertNodes(editor, image);
};

const insertMention = (editor, member) => {
  if (!editor.selection) return;

  // Remove the "@word" that triggered the picker (same as you already do)
  const { selection } = editor;
  const [start] = Range.edges(selection);
  const wordBefore = Editor.before(editor, start, { unit: 'word' });
  const before = wordBefore && Editor.before(editor, wordBefore);
  const beforeRange = before && Editor.range(editor, before, start);
  const beforeText = beforeRange && Editor.string(editor, beforeRange);
  const charBefore = Editor.before(editor, start, { unit: 'character' });
  const charRange = charBefore && Editor.range(editor, charBefore, start);
  const charText = charRange && Editor.string(editor, charRange);

  if (beforeText && beforeText.startsWith('@')) {
    Transforms.delete(editor, { at: beforeRange });
  } else if (charText === '@') {
    Transforms.delete(editor, { at: charRange });
  }

  // Insert the inline-void mention
  const mention = {
    type: 'mention',
    character: member.value,
    id: member.id,
    children: [{ text: '' }]
  };
  Transforms.insertNodes(editor, mention);

  // ⬅️ Critical: leave the void first
  Transforms.move(editor); // moves the cursor to just after the void

  // Now add a *real* Slate space node and keep the caret after it
  Transforms.insertText(editor, ' ');

  ReactEditor.focus(editor);
};

const withImages = (editor) => {
  const { isVoid } = editor;
  editor.isVoid = (element) => (element.type === 'image' ? true : isVoid(element));
  return editor;
};

const withMentions = (editor) => {
  const { isVoid, isInline } = editor;

  editor.isVoid = (element) => {
    return element.type === 'mention' ? true : isVoid(element);
  };

  editor.isInline = (element) => {
    return element.type === 'mention' ? true : isInline(element);
  };

  return editor;
};

const renderElement = (props) => {
  const { element, attributes, children } = props;
  switch (element.type) {
    case 'image':
      return <img {...attributes} src={element.url} alt="" className={style.image} />;
    case 'mention':
      return (
        <>
          <span
            {...attributes}
            contentEditable={false}
            className={style.mention}
            data-value={element.character}
            data-id={element.id}>
            @{element.character}
          </span>
          {children}
        </>
      );
    case 'bulleted-list':
      return <ul {...attributes}>{children}</ul>;
    case 'numbered-list':
      return <ol {...attributes}>{children}</ol>;
    case 'list-item':
      return <li {...attributes}>{children}</li>;
    case 'link':
      return (
        <a {...attributes} href={element.url} className={style.link} target="_blank" rel="noopener noreferrer">
          {children}
        </a>
      );
    default:
      return <p {...attributes}>{children}</p>;
  }
};

const insertLink = (editor, url) => {
  if (!url) return;

  if (editor.selection) {
    const isCollapsed = Range.isCollapsed(editor.selection);

    const link = {
      type: 'link',
      url,
      children: isCollapsed ? [{ text: url }] : []
    };

    if (isCollapsed) {
      Transforms.insertNodes(editor, link);
    } else {
      Transforms.wrapNodes(editor, link, { split: true });
      Transforms.collapse(editor, { edge: 'end' });
    }
  }
};

export default function SlateEditor({ value, onChange, error, members, minimalToolbar = false }) {
  const { user } = useAuth();
  const reactTextEditor = useRef();
  const [editor] = useState(() => withMentions(withImages(withHistory(withReact(createEditor())))));
  const [target, setTarget] = useState();
  const [index, setIndex] = useState(0);
  const [search, setSearch] = useState('');
  const [teamMembers, setTeamMembers] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const editableRef = useRef(null);

  useEffect(() => {
    if (editor && editor.children?.length > 0) {
      ReactEditor.focus(editor);

      // Move the cursor to the end
      const end = Editor.end(editor, []);
      Transforms.select(editor, end);
    }
  }, [editor]);

  const initialValue = [
    {
      type: 'paragraph',
      children: [{ text: '' }]
    }
  ];
  const filteredMentions = teamMembers?.filter((member) => member?.value?.toLowerCase().includes(search?.toLowerCase()));

  useEffect(() => {
    if (members) {
      let formatted = members.filter((m) => m._id !== user?._id).map((m) => ({ id: m._id, value: m.fullName }));
      setTeamMembers(formatted);
    }
  }, [members, user]);

  const renderLeaf = useCallback((props) => <Leaf {...props} />, []);

  const handleKeyDown = (event) => {
    try {
      for (const hotkey in HOTKEYS) {
        if (isHotkey(hotkey, event)) {
          event.preventDefault();
          toggleFormat(editor, HOTKEYS[hotkey]);
        }
      }

      if (target) {
        switch (event.key) {
          case 'ArrowDown':
            event.preventDefault();
            setIndex((i) => (i + 1) % filteredMentions.length);
            break;
          case 'ArrowUp':
            event.preventDefault();
            setIndex((i) => (i - 1 + filteredMentions.length) % filteredMentions.length);
            break;
          case 'Enter':
            event.preventDefault();
            if (filteredMentions.length > 0) {
              // Store the current selection before inserting
              const currentSelection = editor.selection;

              insertMention(editor, filteredMentions[index]);
              setTarget(null);
              setIndex(0);
              setSearch('');

              // // Use requestAnimationFrame for more reliable focus restoration
              // requestAnimationFrame(() => {
              //   try {
              //     ReactEditor.focus(editor);
              //     // Try to restore cursor position after the mention
              //     if (currentSelection) {
              //       const newPath = [...currentSelection.anchor.path];
              //       const newOffset = currentSelection.anchor.offset + 1;
              //       Transforms.select(editor, {
              //         anchor: { path: newPath, offset: newOffset },
              //         focus: { path: newPath, offset: newOffset }
              //       });
              //     }
              //   } catch (error) {
              //     console.error('Error restoring focus:', error);
              //     // Fallback: just focus the editor
              //     ReactEditor.focus(editor);
              //   }
              // });
            }
            break;
          case 'Escape':
            setTarget(null);
            setIndex(0);
            setSearch('');
            break;
          case ' ':
            setTarget(null);
            setIndex(0);
            setSearch('');
            break;
          default:
            break;
        }
      }

      if (event.key === '@') {
        const { selection } = editor;
        if (selection) {
          setTarget(selection);
          setSearch('');
          setIndex(0);
        }
      }
      if (event.key === 'Backspace' && target) {
        const { selection } = editor;
        if (selection && Range.isCollapsed(selection)) {
          const [start] = Range.edges(selection);
          const before = Editor.before(editor, start, { unit: 'character' });
          const beforeRange = before && Editor.range(editor, before, start);
          const beforeText = beforeRange && Editor.string(editor, beforeRange);

          if (!beforeText || !beforeText.startsWith('@')) {
            // ❌ the "@" trigger was deleted → close dropdown
            setTarget(null);
            setSearch('');
            setIndex(0);
          }
        }
      }
    } catch (error) {
      console.error('Error in handleKeyDown:', error);
    }
  };

  const handleChange = (val) => {
    try {
      onChange(val);

      // Update search term when typing after @
      if (target) {
        const { selection } = editor;
        if (selection && Range.isCollapsed(selection)) {
          const [start] = Range.edges(selection);
          const wordBefore = Editor.before(editor, start, { unit: 'word' });
          const before = wordBefore && Editor.before(editor, wordBefore);
          const beforeRange = before && Editor.range(editor, before, start);
          const beforeText = beforeRange && Editor.string(editor, beforeRange);
          const beforeMatch = beforeText && beforeText.match(/^@(\w*)$/);
          if (beforeMatch) {
            setSearch(beforeMatch[1]); // could be "" if just "@"
            setTarget(beforeRange); // store full range (even only "@")
          }
        }
      }
    } catch (error) {
      console.error('Error in handleChange:', error);
    }
  };

  const handleImageUpload = async () => {
    const input = document.createElement('input');
    input.setAttribute('type', 'file');
    input.setAttribute('accept', 'image/*');
    input.click();
    input.onchange = async () => {
      if (input.files) {
        setIsLoading(true);
        try {
          const formData = new FormData();
          [...input.files].forEach((file) => formData.append('attachments', file));

          const res = await APIServices.post('/project-milestone/upload', formData, {
            'Content-Type': 'multipart/form-data'
          });

          if (res?.data?.[0]?.url) {
            insertImage(editor, res.data[0].url);
          }
        } catch (err) {
          console.error('Image upload failed', err);
        } finally {
          setIsLoading(false);
        }
      }
    };
  };

  const handleMentionSelect = (member) => {
    try {
      if (target) {
        // Force-select the stored range (the "@something" text)
        Transforms.select(editor, target);
        Transforms.delete(editor); // delete that range explicitly
      }

      // Now safely insert the mention
      insertMention(editor, member);

      setTarget(null);
      setIndex(0);
      setSearch('');

      ReactEditor.focus(editor);
    } catch (error) {
      console.error('Error in handleMentionSelect:', error);
    }
  };

  const urlRegex = /(https?:\/\/[^\s]+)/g;

  const handlePaste = async (event) => {
    const pastedText = (event.clipboardData || window.clipboardData).getData('text');

    if (urlRegex.test(pastedText)) {
      event.preventDefault();

      try {
        setIsLoading(true);

        const response = await APIServices.post('/link/preview', { url: pastedText });
        const title = response?.title || pastedText;

        const linkNode = {
          type: 'link',
          url: pastedText,
          children: [{ text: title }]
        };

        if (editor.selection) {
          // ✅ Insert link directly at the cursor
          Transforms.insertNodes(editor, linkNode);

          // ✅ add a space (so typing continues outside the link)
          Transforms.insertText(editor, ' ');
        }
        // ✅ always add a paragraph after
        Transforms.insertNodes(editor, {
          type: 'paragraph',
          children: [{ text: '' }]
        });
      } catch (err) {
        console.error('Error fetching link preview:', err);
      } finally {
        setIsLoading(false);
      }
    }
  };

  const editorValue = useMemo(() => {
    return Array.isArray(value) && value.length > 0 ? value : initialValue;
  }, [value]);

  return (
    <div
      className={style.text_editor_wrapper}
      onClick={() => {
        // Ensure editor is focused when clicking anywhere in the wrapper
        ReactEditor.focus(editor);
      }}>
      {isLoading && <div className={style.text_editor_loader}>{selectLoader(60)}</div>}
      {editorValue && Array.isArray(editorValue) && (
        <Slate ref={reactTextEditor} editor={editor} initialValue={editorValue} onChange={handleChange}>
          {!minimalToolbar && <Toolbar onImageUpload={handleImageUpload} />}
          <Editable
            ref={editableRef}
            renderElement={renderElement}
            renderLeaf={renderLeaf}
            placeholder=""
            onKeyDown={handleKeyDown}
            onPaste={handlePaste}
            className={`${style.editable} ${error ? 'form-invalid' : ''}`}
          />
          <MentionDropdown
            target={target}
            filteredMentions={filteredMentions}
            index={index}
            onSelect={handleMentionSelect}
          />
        </Slate>
      )}
      {error && <p className="text-danger">{error}</p>}
    </div>
  );
}
